import { useEffect, useRef } from "react";
import type { InlineFieldProps } from "./types";
import { INLINE_INPUT_CLASS } from "./inline-field-styles";
import { cn } from "@/lib/utils";

const MAX_HEIGHT = 240; // ~10 rows

function autoResize(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
}

/**
 * Expandable textarea for Text Editor, Small Text, Long Text, Code fields.
 *
 * - Auto-focuses on mount
 * - Auto-resizes height to fit content (min 3 rows, max ~10 rows)
 * - Commits on blur (Tab also triggers blur)
 * - Enter inserts newline (does NOT commit — textarea behavior)
 * - Cancels on Escape
 * - Double-commit guard via committingRef
 */
export function InlineFieldTextarea({
  field,
  value,
  onCommit,
  onCancel,
  placeholder,
}: InlineFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const committingRef = useRef(false);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      // Move cursor to end
      el.selectionStart = el.value.length;
      el.selectionEnd = el.value.length;
      autoResize(el);
    }
  }, []);

  const handleBlur = () => {
    if (committingRef.current) return;
    committingRef.current = true;
    const newValue = textareaRef.current?.value ?? "";
    const oldValue = String(value ?? "");
    if (newValue !== oldValue) {
      onCommit(newValue);
    } else {
      onCancel();
    }
  };

  const handleInput = () => {
    autoResize(textareaRef.current);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
    // Enter inserts newline (default textarea behavior — no preventDefault)
    // Tab → blur → handleBlur (commits)
  };

  return (
    <textarea
      ref={textareaRef}
      defaultValue={String(value ?? "")}
      onBlur={handleBlur}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      className={cn(
        INLINE_INPUT_CLASS,
        "resize-none min-h-[72px] max-h-[240px] text-left"
      )}
      placeholder={placeholder || `Add ${field.label?.toLowerCase() ?? "text"}...`}
      rows={3}
    />
  );
}
